import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { CheckCircle, Circle, Clock, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';
import { API, STAGES, ACTIVITY_TYPES } from './constants';

const TABS = ['details', 'activities', 'tasks'];

const StageProgress = ({ client, currentStageIndex, onMoveStage }) => (
  <div className="flex items-center gap-2 py-4 overflow-x-auto">
    {STAGES.map((stage, index) => (
      <button
        key={stage.id}
        onClick={() => onMoveStage(client.crm_id, stage.id)}
        className={`flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-widest whitespace-nowrap transition-colors ${
          index <= currentStageIndex ? `${stage.color} text-white` : 'bg-[#050505] text-white/40'
        }`}
      >
        {index < currentStageIndex ? (
          <CheckCircle size={14} />
        ) : index === currentStageIndex ? (
          <Circle size={14} className="fill-current" />
        ) : (
          <Circle size={14} />
        )}
        {stage.label}
      </button>
    ))}
  </div>
);

const DetailsTab = ({ client, onDelete }) => (
  <div className="space-y-6 pr-4">
    <div className="grid grid-cols-2 gap-6">
      {[
        ['Event Type', client.event_type],
        ['Event Date', client.event_date || 'TBD'],
        ['Budget Range', client.budget_range || 'Not specified'],
        ['Phone', client.phone || 'Not provided'],
        ['Venue', client.venue || 'TBD'],
        ['Source', client.source],
      ].map(([label, value]) => (
        <div key={label}>
          <p className="text-white/40 text-xs uppercase tracking-widest mb-1">{label}</p>
          <p className={`text-white ${label === 'Source' ? 'capitalize' : ''}`}>{value}</p>
        </div>
      ))}
    </div>
    {client.services_needed?.length > 0 && (
      <div>
        <p className="text-white/40 text-xs uppercase tracking-widest mb-2">Services Needed</p>
        <div className="flex flex-wrap gap-2">
          {client.services_needed.map((service) => (
            <span key={service} className="text-xs bg-[#050505] text-white/80 px-3 py-1">
              {service}
            </span>
          ))}
        </div>
      </div>
    )}
    {client.notes && (
      <div>
        <p className="text-white/40 text-xs uppercase tracking-widest mb-2">Notes</p>
        <p className="text-white/80 leading-relaxed">{client.notes}</p>
      </div>
    )}
    <div className="pt-4 border-t border-white/10">
      <button
        onClick={onDelete}
        className="text-red-500 hover:text-red-600 text-sm flex items-center gap-2"
      >
        <Trash2 size={14} /> Delete Client
      </button>
    </div>
  </div>
);

const ActivitiesTab = ({ activities, newActivity, setNewActivity, onAdd }) => (
  <div className="space-y-4 pr-4">
    <div className="bg-[#050505] border border-white/10 p-4 space-y-3">
      <div className="flex gap-2">
        {ACTIVITY_TYPES.map((type) => (
          <button
            key={type.id}
            onClick={() => setNewActivity({ ...newActivity, type: type.id })}
            className={`p-2 transition-colors ${
              newActivity.type === type.id
                ? 'bg-[#D4AF37] text-white'
                : 'bg-[#0a0a0a] text-white/60 hover:text-white'
            }`}
            title={type.label}
          >
            <type.icon size={16} />
          </button>
        ))}
      </div>
      <textarea
        value={newActivity.description}
        onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })}
        placeholder="Add a note, log a call, etc..."
        className="w-full bg-[#0a0a0a] border border-white/20 p-3 text-white placeholder:text-[#B8AEA1] focus:outline-none focus:border-[#D4AF37] transition-colors min-h-[80px] resize-none"
      />
      <button
        onClick={onAdd}
        disabled={!newActivity.description.trim()}
        className="px-4 py-2 bg-[#1B1B1B] text-white text-xs uppercase tracking-widest hover:bg-[#111111] transition-colors disabled:opacity-50"
      >
        Add Activity
      </button>
    </div>

    <div className="space-y-3">
      {activities.map((activity) => {
        const typeInfo = ACTIVITY_TYPES.find((t) => t.id === activity.activity_type) || ACTIVITY_TYPES[0];
        return (
          <div key={activity.activity_id} className="flex gap-3 p-3 bg-[#050505]">
            <div className="p-2 bg-[#0a0a0a] h-fit">
              <typeInfo.icon size={14} className="text-[#D4AF37]" />
            </div>
            <div className="flex-1">
              <p className="text-white/80 text-sm">{activity.description}</p>
              <p className="text-white/40 text-xs mt-1">
                {activity.created_by_name} • {format(new Date(activity.created_at), 'MMM d, yyyy h:mm a')}
              </p>
            </div>
          </div>
        );
      })}
      {activities.length === 0 && (
        <p className="text-white/40 text-center py-8">No activities yet</p>
      )}
    </div>
  </div>
);

const TasksTab = ({ tasks, newTask, setNewTask, onAdd, onComplete }) => (
  <div className="space-y-4 pr-4">
    <div className="bg-[#050505] border border-white/10 p-4 space-y-3">
      <input
        type="text"
        value={newTask.title}
        onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
        placeholder="Task title..."
        className="w-full bg-[#0a0a0a] border-b border-white/20 py-3 text-white placeholder:text-[#B8AEA1] focus:outline-none focus:border-[#D4AF37] transition-colors"
      />
      <div className="flex gap-4">
        <div className="flex-1">
          <label className="text-white/40 text-xs">Due Date</label>
          <input
            type="date"
            value={newTask.due_date}
            onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
            className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:outline-none focus:border-[#D4AF37] transition-colors"
          />
        </div>
        <div className="flex-1">
          <label className="text-white/40 text-xs">Priority</label>
          <Select
            value={newTask.priority}
            onValueChange={(value) => setNewTask({ ...newTask, priority: value })}
          >
            <SelectTrigger className="w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 focus:ring-0 text-white h-auto">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0a0a0a] border-white/10">
              {['low', 'medium', 'high'].map((p) => (
                <SelectItem key={p} value={p} className="text-white/70 capitalize">
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <button
        onClick={onAdd}
        disabled={!newTask.title.trim()}
        className="px-4 py-2 bg-[#1B1B1B] text-white text-xs uppercase tracking-widest hover:bg-[#111111] transition-colors disabled:opacity-50"
      >
        Add Task
      </button>
    </div>

    <div className="space-y-2">
      {tasks.map((task) => (
        <div
          key={task.task_id}
          className={`flex items-center gap-3 p-3 transition-colors ${
            task.completed ? 'bg-[#050505] opacity-60' : 'bg-[#0a0a0a] border border-white/10'
          }`}
        >
          <button
            onClick={() => onComplete(task.task_id)}
            className={`p-1 ${
              task.completed ? 'text-green-500' : 'text-white/40 hover:text-white'
            }`}
          >
            {task.completed ? <CheckCircle size={18} /> : <Circle size={18} />}
          </button>
          <div className="flex-1">
            <p className={`text-sm ${task.completed ? 'line-through text-white/40' : 'text-white'}`}>
              {task.title}
            </p>
            {task.due_date && (
              <p className="text-white/40 text-xs flex items-center gap-1">
                <Clock size={12} />
                Due {task.due_date}
              </p>
            )}
          </div>
          <span
            className={`text-xs px-2 py-1 ${
              task.priority === 'high'
                ? 'bg-red-50 text-red-500'
                : task.priority === 'medium'
                ? 'bg-yellow-50 text-yellow-600'
                : 'bg-[#050505] text-white/60'
            }`}
          >
            {task.priority}
          </span>
        </div>
      ))}
      {tasks.length === 0 && <p className="text-white/40 text-center py-8">No tasks yet</p>}
    </div>
  </div>
);

const ConvertToPortalModal = ({ open, onClose, client, password, setPassword, onConfirm }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-md">
      <DialogHeader>
        <DialogTitle className="font-heading text-xl text-white">Create Portal Access</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 mt-4">
        <p className="text-white/60 text-sm">
          This will create a login account for{' '}
          <strong className="text-white">{client?.name}</strong> so they can access the client portal.
        </p>
        <div>
          <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">
            Temporary Password
          </label>
          <input
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-[#B8AEA1] focus:outline-none focus:border-[#D4AF37] transition-colors"
            placeholder="Enter a temporary password"
          />
        </div>
        <p className="text-white/40 text-xs">
          Share this password securely with the client. They can change it after logging in.
        </p>
        <button
          onClick={onConfirm}
          className="w-full py-4 bg-[#1B1B1B] text-white text-sm uppercase tracking-widest hover:bg-[#111111] transition-colors"
        >
          Create Access
        </button>
      </div>
    </DialogContent>
  </Dialog>
);

export const ClientDetailModal = ({ open, onClose, client, onUpdate, onMoveStage }) => {
  const [activities, setActivities] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [activeTab, setActiveTab] = useState('details');
  const [newActivity, setNewActivity] = useState({ type: 'note', description: '' });
  const [newTask, setNewTask] = useState({ title: '', due_date: '', priority: 'medium' });
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertPassword, setConvertPassword] = useState('');

  const fetchClientData = useCallback(async () => {
    if (!client) return;
    try {
      const [activitiesRes, tasksRes] = await Promise.all([
        axios.get(`${API}/crm/clients/${client.crm_id}/activities`, { withCredentials: true }),
        axios.get(`${API}/crm/clients/${client.crm_id}/tasks`, { withCredentials: true }),
      ]);
      setActivities(activitiesRes.data);
      setTasks(tasksRes.data);
    } catch {
      console.error('Failed to fetch client data');
    }
  }, [client]);

  useEffect(() => {
    if (client && open) {
      fetchClientData();
    }
  }, [client, open, fetchClientData]);

  const addActivity = async () => {
    if (!newActivity.description.trim()) return;
    try {
      await axios.post(
        `${API}/crm/clients/${client.crm_id}/activities`,
        { crm_id: client.crm_id, activity_type: newActivity.type, description: newActivity.description },
        { withCredentials: true },
      );
      setNewActivity({ type: 'note', description: '' });
      fetchClientData();
      toast.success('Activity added');
    } catch {
      toast.error('Failed to add activity');
    }
  };

  const addTask = async () => {
    if (!newTask.title.trim()) return;
    try {
      await axios.post(
        `${API}/crm/clients/${client.crm_id}/tasks`,
        { crm_id: client.crm_id, ...newTask },
        { withCredentials: true },
      );
      setNewTask({ title: '', due_date: '', priority: 'medium' });
      fetchClientData();
      toast.success('Task added');
    } catch {
      toast.error('Failed to add task');
    }
  };

  const completeTask = async (taskId) => {
    try {
      await axios.patch(`${API}/crm/tasks/${taskId}/complete`, {}, { withCredentials: true });
      fetchClientData();
    } catch {
      toast.error('Failed to complete task');
    }
  };

  const convertToPortal = async () => {
    if (!convertPassword.trim()) {
      toast.error('Please enter a password');
      return;
    }
    try {
      await axios.post(
        `${API}/crm/clients/${client.crm_id}/convert?password=${encodeURIComponent(convertPassword)}`,
        {},
        { withCredentials: true },
      );
      toast.success('Portal access created! Client can now login.');
      setShowConvertModal(false);
      setConvertPassword('');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create portal access');
    }
  };

  const deleteClient = async () => {
    if (!window.confirm('Are you sure you want to delete this client? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/crm/clients/${client.crm_id}`, { withCredentials: true });
      toast.success('Client deleted');
      onClose();
      onUpdate();
    } catch {
      toast.error('Failed to delete client');
    }
  };

  if (!client) return null;

  const currentStageIndex = STAGES.findIndex((s) => s.id === client.stage);

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <div className="flex items-start justify-between">
              <div>
                <DialogTitle className="font-heading text-2xl text-white">{client.name}</DialogTitle>
                <p className="text-white/60 text-sm mt-1">{client.email}</p>
              </div>
              <div className="flex items-center gap-2">
                {!client.user_id && (
                  <button
                    onClick={() => setShowConvertModal(true)}
                    className="text-xs py-2 px-4 border border-white/20 text-white hover:border-[#1B1B1B] transition-colors"
                  >
                    Create Portal Access
                  </button>
                )}
                {client.user_id && (
                  <span className="text-xs bg-green-50 text-green-600 px-3 py-1">Has Portal Access</span>
                )}
              </div>
            </div>
          </DialogHeader>

          <StageProgress
            client={client}
            currentStageIndex={currentStageIndex}
            onMoveStage={onMoveStage}
          />

          <div className="flex gap-4 border-b border-white/10">
            {TABS.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-3 text-sm capitalize transition-colors ${
                  activeTab === tab
                    ? 'text-[#D4AF37] border-b-2 border-[#C6A86A]'
                    : 'text-white/60 hover:text-white'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <ScrollArea className="flex-1 mt-4">
            {activeTab === 'details' && <DetailsTab client={client} onDelete={deleteClient} />}
            {activeTab === 'activities' && (
              <ActivitiesTab
                activities={activities}
                newActivity={newActivity}
                setNewActivity={setNewActivity}
                onAdd={addActivity}
              />
            )}
            {activeTab === 'tasks' && (
              <TasksTab
                tasks={tasks}
                newTask={newTask}
                setNewTask={setNewTask}
                onAdd={addTask}
                onComplete={completeTask}
              />
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <ConvertToPortalModal
        open={showConvertModal}
        onClose={setShowConvertModal}
        client={client}
        password={convertPassword}
        setPassword={setConvertPassword}
        onConfirm={convertToPortal}
      />
    </>
  );
};
